import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import PageHeader from './components/PageHeader';
import FieldTypeIconPicker from './components/FieldTypeIconPicker';
import { apiFetch } from './utils/api';

const FIELD_TYPES = [
  { value: 'text', label: 'Text' },
  { value: 'multiline', label: 'Multi-line Text' },
  { value: 'number', label: 'Number' },
  { value: 'currency', label: 'Currency' },
  { value: 'checkbox', label: 'Checkbox / Tick' },
  { value: 'yes_no', label: 'Yes / No' },
  { value: 'dropdown', label: 'Dropdown' },
  { value: 'multi_select', label: 'Multi-select' },
  { value: 'date', label: 'Date' },
  { value: 'time', label: 'Time' },
  { value: 'datetime', label: 'Date + Time' },
  { value: 'signature', label: 'Signature' },
  { value: 'photo', label: 'Photo' },
  { value: 'initials', label: 'Initials' },
  { value: 'gps_location', label: 'GPS Location' },
  { value: 'asset_lookup', label: 'Asset Lookup' },
  { value: 'part_lookup', label: 'Part Lookup' },
];

const OPTION_FIELD_TYPES = new Set(['dropdown', 'multi_select']);

const FALLBACK_DOCUMENT_TYPES = [
  { value: 'risk_assessment', label: 'Risk Assessment' },
  { value: 'service_report', label: 'Service Report' },
  { value: 'completion_certificate', label: 'Completion Certificate' },
  { value: 'job_sheet', label: 'Job Sheet' },
  { value: 'compliance_forms', label: 'Compliance Forms' },
];

const EMPTY_FORM = {
  id: '',
  name: '',
  form_key: '',
  description: '',
  document_type: 'risk_assessment',
  questions: [],
};

function slugFromText(value, fallback = 'question') {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 80) || fallback;
}

function normaliseQuestion(question, index) {
  const label = question.label || '';
  return {
    id: question.id || `new-${Date.now()}-${index}`,
    label,
    question_key: question.question_key || slugFromText(label, `question_${index + 1}`),
    section: question.section || 'General',
    help_text: question.help_text || '',
    field_type: FIELD_TYPES.some((type) => type.value === question.field_type) ? question.field_type : 'text',
    required: question.required === true,
    options: Array.isArray(question.options) ? question.options : [],
    sort_order: Number.isFinite(Number(question.sort_order)) ? Number(question.sort_order) : index,
    merge_field: question.merge_field || '',
  };
}

function normaliseForm(form) {
  return {
    ...EMPTY_FORM,
    ...form,
    questions: Array.isArray(form?.questions) ? form.questions.map(normaliseQuestion) : [],
  };
}

function optionsToText(options) {
  return Array.isArray(options) ? options.join('\n') : '';
}

function textToOptions(text) {
  return String(text || '')
    .split(/\r?\n/)
    .map((option) => option.trim())
    .filter(Boolean);
}

function FormDesignerPage() {
  const [searchParams] = useSearchParams();
  const [forms, setForms] = useState([]);
  const [activeLibrary, setActiveLibrary] = useState('');
  const [selectedId, setSelectedId] = useState('');
  const [draft, setDraft] = useState(EMPTY_FORM);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [status, setStatus] = useState('');
  const [error, setError] = useState('');
  const [documentTypes, setDocumentTypes] = useState(FALLBACK_DOCUMENT_TYPES);

  const selectedForm = useMemo(() => forms.find((form) => form.id === selectedId) || null, [forms, selectedId]);
  const requestedFormId = searchParams.get('form') || '';

  const formCounts = useMemo(() => {
    return forms.reduce((acc, form) => {
      const type = form.document_type || 'risk_assessment';
      acc[type] = (acc[type] || 0) + 1;
      return acc;
    }, {});
  }, [forms]);

  const activeLibraryMeta = useMemo(() => {
    return documentTypes.find((type) => type.value === activeLibrary) || null;
  }, [activeLibrary, documentTypes]);

  const visibleForms = useMemo(() => {
    if (!activeLibrary) return [];
    return forms.filter((form) => (form.document_type || 'risk_assessment') === activeLibrary);
  }, [activeLibrary, forms]);

  const documentTypeOptions = useMemo(() => {
    const hasDraftType = draft.document_type && !documentTypes.some((type) => type.value === draft.document_type);
    if (!hasDraftType) return documentTypes;
    return [
      ...documentTypes,
      { value: draft.document_type, label: draft.document_type.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase()) },
    ];
  }, [documentTypes, draft.document_type]);

  const loadDocumentTypes = useCallback(async () => {
    try {
      const res = await apiFetch('/api/documents/document-types');
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to load document libraries.');
      const nextTypes = Array.isArray(data) && data.length
        ? data.map((item) => ({
            value: item.value || item.key,
            label: item.label || item.value || item.key,
          })).filter((item) => item.value)
        : FALLBACK_DOCUMENT_TYPES;
      setDocumentTypes(nextTypes);
      setDraft((current) => {
        if (current.id || current.document_type) return current;
        return { ...current, document_type: nextTypes[0]?.value || 'risk_assessment' };
      });
    } catch {
      setDocumentTypes(FALLBACK_DOCUMENT_TYPES);
    }
  }, []);

  const loadForms = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await apiFetch('/api/documents/forms');
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to load forms.');
      const nextForms = Array.isArray(data) ? data.map(normaliseForm) : [];
      setForms(nextForms);
      const requestedForm = requestedFormId ? nextForms.find((form) => form.id === requestedFormId) : null;
      if (requestedForm) setActiveLibrary(requestedForm.document_type || 'risk_assessment');
      setSelectedId((current) => {
        if (requestedForm) return requestedForm.id;
        return nextForms.some((form) => form.id === current) ? current : '';
      });
      return nextForms;
    } catch (err) {
      setError(err.message || 'Failed to load forms.');
      return [];
    } finally {
      setLoading(false);
    }
  }, [requestedFormId]);

  useEffect(() => {
    loadForms();
    loadDocumentTypes();
  }, [loadForms, loadDocumentTypes]);

  useEffect(() => {
    setDraft(selectedForm ? normaliseForm(selectedForm) : EMPTY_FORM);
  }, [selectedForm]);

  function startNewForm() {
    const defaultDocumentType = activeLibrary || documentTypes[0]?.value || 'risk_assessment';
    const defaultLibrary = documentTypes.find((type) => type.value === defaultDocumentType);
    if (!activeLibrary) setActiveLibrary(defaultDocumentType);
    setSelectedId('');
    setDraft({
      ...EMPTY_FORM,
      name: defaultLibrary?.label || 'Risk Assessment',
      form_key: defaultDocumentType,
      document_type: defaultDocumentType,
      questions: [],
    });
    setStatus('');
    setError('');
  }

  function updateQuestion(index, patch) {
    setDraft((current) => ({
      ...current,
      questions: current.questions.map((question, questionIndex) => {
        if (questionIndex !== index) return question;
        const next = { ...question, ...patch };
        if (patch.label !== undefined && !question.question_key) {
          next.question_key = slugFromText(patch.label, `question_${index + 1}`);
        }
        return next;
      }),
    }));
  }

  function addQuestion() {
    setDraft((current) => ({
      ...current,
      questions: [
        ...current.questions,
        normaliseQuestion({
          label: '',
          question_key: '',
          section: 'General',
          field_type: 'text',
          required: false,
          sort_order: current.questions.length,
        }, current.questions.length),
      ],
    }));
  }

  function removeQuestion(index) {
    setDraft((current) => ({
      ...current,
      questions: current.questions.filter((_, questionIndex) => questionIndex !== index),
    }));
  }

  async function saveForm() {
    const name = draft.name.trim();
    if (!name) {
      setError('Form name is required.');
      return;
    }
    setSaving(true);
    setError('');
    setStatus('');
    try {
      const payload = {
        ...draft,
        name,
        form_key: slugFromText(draft.form_key || name, 'form'),
        questions: draft.questions
          .map((question, index) => ({
            ...question,
            label: String(question.label || '').trim(),
            question_key: slugFromText(question.question_key || question.label, `question_${index + 1}`),
            section: question.section || 'General',
            sort_order: index,
          }))
          .filter((question) => question.label),
      };
      const url = draft.id ? `/api/documents/forms/${draft.id}` : '/api/documents/forms';
      const res = await apiFetch(url, {
        method: draft.id ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to save form.');

      if (draft.id) {
        setForms((current) => current.map((form) => (form.id === data.id ? normaliseForm(data) : form)));
      } else {
        setForms((current) => [...current, normaliseForm(data)]);
      }
      setActiveLibrary(data.document_type || activeLibrary);
      setSelectedId(data.id);
      setDraft(normaliseForm(data));
      setStatus('Question list saved. Its merge fields are now available in the PDF overlay builder.');
    } catch (err) {
      setError(err.message || 'Failed to save form.');
    } finally {
      setSaving(false);
    }
  }

  async function deleteForm() {
    if (!draft.id) return;
    setSaving(true);
    setError('');
    setStatus('');
    try {
      const res = await apiFetch(`/api/documents/forms/${draft.id}`, { method: 'DELETE' });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Failed to delete form.');
      const remaining = forms.filter((form) => form.id !== draft.id);
      setForms(remaining);
      setSelectedId(remaining[0]?.id || '');
      setStatus('Question list deleted.');
    } catch (err) {
      setError(err.message || 'Failed to delete form.');
    } finally {
      setSaving(false);
    }
  }

  async function seedMobileForms() {
    setSeeding(true);
    setError('');
    setStatus('');
    try {
      const res = await apiFetch('/api/documents/forms/seed-mobile', { method: 'POST' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to add starter lists.');
      const nextForms = await loadForms();
      const firstSeeded = Array.isArray(data.seeded) ? data.seeded[0] : null;
      const firstEn16005 = nextForms.find((form) => form.form_key === 'en16005_sliding_door_compliance')
        || nextForms.find((form) => form.form_key === 'en16005_swing_door_compliance');
      setActiveLibrary('compliance_forms');
      if (firstSeeded?.id) setSelectedId(firstSeeded.id);
      else if (firstEn16005?.id) setSelectedId(firstEn16005.id);
      setStatus(data.message || 'Starter question lists added.');
    } catch (err) {
      setError(err.message || 'Failed to add starter lists.');
    } finally {
      setSeeding(false);
    }
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <PageHeader
        title="Form Question Lists"
        subtitle="Build mobile form questions and generate merge fields for PDF overlay templates"
        backTo="/design-hub"
        backLabel="Design Hub"
        sticky
      />

      <main className="max-w-7xl mx-auto px-6 py-8 space-y-5">
        {(error || status) && (
          <div className={`rounded-lg border px-4 py-3 text-sm ${error ? 'border-red-200 bg-red-50 text-red-700' : 'border-green-200 bg-green-50 text-green-700'}`}>
            {error || status}
          </div>
        )}

        <section className="grid gap-5 lg:grid-cols-[240px_300px_minmax(0,1fr)]">
          <aside className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
            <h2 className="text-sm font-semibold text-gray-900">Libraries</h2>
            <p className="mt-1 text-xs text-gray-500">Loaded from Document Library Setup.</p>
            <div className="mt-4 space-y-2">
              {documentTypes.map((type) => {
                const count = formCounts[type.value] || 0;
                return (
                  <button
                    key={type.value}
                    type="button"
                    onClick={() => {
                      setActiveLibrary(type.value);
                      const first = forms.find((form) => (form.document_type || 'risk_assessment') === type.value);
                      setSelectedId(first?.id || '');
                      setStatus('');
                      setError('');
                    }}
                    className={`w-full rounded-md border px-3 py-2 text-left text-sm ${
                      activeLibrary === type.value ? 'border-blue-500 bg-blue-50' : 'border-gray-200 bg-white hover:bg-gray-50'
                    }`}
                  >
                    <span className="block font-semibold text-gray-900">{type.label}</span>
                    <span className="text-xs text-gray-500">{count} question lists</span>
                  </button>
                );
              })}
            </div>
          </aside>

          <aside className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
            <div className="flex items-center justify-between gap-3">
              <div>
                <h2 className="text-sm font-semibold text-gray-900">Question Lists</h2>
                <p className="mt-1 text-xs text-gray-500">
                  {activeLibraryMeta ? activeLibraryMeta.label : 'Select a library'}
                </p>
              </div>
              <div className="flex items-center gap-2">
                {activeLibrary === 'compliance_forms' && (
                  <button
                    type="button"
                    onClick={seedMobileForms}
                    disabled={seeding}
                    className="rounded-md border border-blue-200 bg-blue-50 px-3 py-1.5 text-xs font-semibold text-blue-700 hover:bg-blue-100 disabled:opacity-50"
                  >
                    {seeding ? 'Adding...' : 'Add EN16005'}
                  </button>
                )}
                <button
                  type="button"
                  onClick={startNewForm}
                  disabled={!activeLibrary}
                  className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-50"
                >
                  New
                </button>
              </div>
            </div>
            <div className="mt-4 space-y-2">
              {visibleForms.map((form) => (
                <button
                  key={form.id}
                  type="button"
                  onClick={() => setSelectedId(form.id)}
                  className={`w-full rounded-md border px-3 py-2 text-left text-sm ${
                    selectedId === form.id ? 'border-blue-500 bg-blue-50' : 'border-gray-200 bg-white hover:bg-gray-50'
                  }`}
                >
                  <span className="block font-semibold text-gray-900">{form.name}</span>
                  <span className="text-xs text-gray-500">{form.questions.length} questions</span>
                </button>
              ))}
              {!activeLibrary && <p className="text-sm text-gray-500">Select a library to see its question lists.</p>}
              {activeLibrary && !visibleForms.length && !loading && (
                <p className="text-sm text-gray-500">No question lists in this library yet.</p>
              )}
            </div>
          </aside>

          <section className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
            {!activeLibrary ? (
              <div className="rounded-lg border border-dashed border-gray-300 p-10 text-center text-sm text-gray-500">
                Select a library on the left to manage its form question lists.
              </div>
            ) : !draft.id && !draft.name ? (
              <div className="rounded-lg border border-dashed border-gray-300 p-10 text-center text-sm text-gray-500">
                Select a question list or create a new one in {activeLibraryMeta?.label || 'this library'}.
              </div>
            ) : (
            <>
            <div className="grid gap-4 md:grid-cols-2">
              <label className="text-xs font-semibold text-gray-600">
                Form Name
                <input
                  value={draft.name}
                  onChange={(event) => setDraft((current) => ({ ...current, name: event.target.value, form_key: current.form_key || slugFromText(event.target.value, 'form') }))}
                  className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                  placeholder="Risk Assessment"
                />
              </label>
              <label className="text-xs font-semibold text-gray-600">
                Function / Library
                <select
                  value={draft.document_type}
                  onChange={(event) => setDraft((current) => ({ ...current, document_type: event.target.value }))}
                  className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                >
                  {documentTypeOptions.map((type) => (
                    <option key={type.value} value={type.value}>{type.label}</option>
                  ))}
                </select>
                <span className="mt-1 block text-xs font-normal text-gray-500">
                  Loaded from Document Library Setup.
                </span>
              </label>
              <label className="text-xs font-semibold text-gray-600">
                Form Key
                <input
                  value={draft.form_key}
                  onChange={(event) => setDraft((current) => ({ ...current, form_key: slugFromText(event.target.value, 'form') }))}
                  className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 font-mono text-sm"
                  placeholder="risk_assessment"
                />
              </label>
              <label className="text-xs font-semibold text-gray-600">
                Description
                <input
                  value={draft.description}
                  onChange={(event) => setDraft((current) => ({ ...current, description: event.target.value }))}
                  className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                  placeholder="Questions engineers complete on site"
                />
              </label>
            </div>

            <div className="mt-6 flex items-center justify-between gap-3">
              <h2 className="text-sm font-semibold text-gray-900">Questions</h2>
              <button
                type="button"
                onClick={addQuestion}
                className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
              >
                Add Question
              </button>
            </div>

            <div className="mt-4 space-y-3">
              {draft.questions.map((question, index) => (
                <div key={question.id} className="rounded-lg border border-gray-200 p-4">
                  <div className="grid gap-3 md:grid-cols-[minmax(0,1.5fr)_110px_auto] md:items-end">
                    <label className="text-xs font-semibold text-gray-600">
                      Question
                      <input
                        value={question.label}
                        onChange={(event) => updateQuestion(index, { label: event.target.value })}
                        className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                        placeholder="Is the work area safe?"
                      />
                    </label>
                    <label className="flex items-center gap-2 rounded-md border border-gray-200 px-3 py-2 text-sm font-semibold text-gray-700">
                      <input
                        type="checkbox"
                        checked={question.required}
                        onChange={(event) => updateQuestion(index, { required: event.target.checked })}
                      />
                      Required
                    </label>
                    <button
                      type="button"
                      onClick={() => removeQuestion(index)}
                      className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm font-semibold text-red-700 hover:bg-red-100"
                    >
                      Remove
                    </button>
                  </div>
                  <div className="mt-3">
                    <FieldTypeIconPicker
                      value={question.field_type}
                      options={FIELD_TYPES}
                      onChange={(value) => updateQuestion(index, {
                        field_type: value,
                        options: OPTION_FIELD_TYPES.has(value) ? question.options : [],
                      })}
                    />
                  </div>
                  {OPTION_FIELD_TYPES.has(question.field_type) && (
                    <label className="mt-3 block text-xs font-semibold text-gray-600">
                      Options
                      <textarea
                        value={optionsToText(question.options)}
                        onChange={(event) => updateQuestion(index, { options: textToOptions(event.target.value) })}
                        className="mt-1 h-24 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                        placeholder="One option per line"
                      />
                    </label>
                  )}
                  <div className="mt-3 grid gap-3 md:grid-cols-2">
                    <label className="text-xs font-semibold text-gray-600">
                      Section
                      <input
                        value={question.section}
                        onChange={(event) => updateQuestion(index, { section: event.target.value })}
                        className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                        placeholder="Site Details"
                      />
                    </label>
                    <label className="text-xs font-semibold text-gray-600">
                      Question Key
                      <input
                        value={question.question_key}
                        onChange={(event) => updateQuestion(index, { question_key: slugFromText(event.target.value, `question_${index + 1}`) })}
                        className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 font-mono text-sm"
                      />
                    </label>
                    <label className="text-xs font-semibold text-gray-600 md:col-span-2">
                      Help Text
                      <input
                        value={question.help_text}
                        onChange={(event) => updateQuestion(index, { help_text: event.target.value })}
                        className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                      />
                    </label>
                  </div>
                  <div className="mt-3 rounded-md border border-gray-200 bg-gray-50 px-3 py-2">
                    <span className="text-xs font-semibold text-gray-600">Merge Field</span>
                    <code className="mt-1 block break-all font-mono text-sm text-gray-900">
                      {question.merge_field || `form_${slugFromText(draft.form_key || draft.name, 'form')}_${slugFromText(question.question_key || question.label, `question_${index + 1}`)}`}
                    </code>
                  </div>
                </div>
              ))}
              {!draft.questions.length && (
                <div className="rounded-lg border border-dashed border-gray-300 p-8 text-center text-sm text-gray-500">
                  Add questions to generate merge fields for the PDF overlay designer.
                </div>
              )}
            </div>

            <div className="mt-5 flex flex-wrap justify-end gap-3">
              {draft.id && (
                <button
                  type="button"
                  onClick={deleteForm}
                  disabled={saving}
                  className="rounded-md border border-red-200 bg-red-50 px-4 py-2 text-sm font-semibold text-red-700 hover:bg-red-100 disabled:opacity-50"
                >
                  Delete
                </button>
              )}
              <button
                type="button"
                onClick={saveForm}
                disabled={saving}
                className="rounded-md bg-[#082547] px-5 py-2 text-sm font-semibold text-white hover:bg-[#0a2d5c] disabled:opacity-50"
              >
                Save Question List
              </button>
            </div>
            </>
            )}
          </section>
        </section>
      </main>
    </div>
  );
}

export default FormDesignerPage;
